import { useEffect, useRef } from 'react';

import { motion } from 'motion/react';

import secureOneLabsLogo from '@src/assets/logo_shield.png';

export default function CompanyBranding({ onlyLogo }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);


  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');

    const parts = [
      { text: 'Secure', color: '#1e638d', weight: 'bold' },
      { text: 'One', color: '#999999', weight: 'normal' },
      { text: 'Labs', color: '#1e638d', weight: 'bold' },
    ];
    const companyName = parts.map((p) => p.text).join('');

    const framesPerChar = 12;
    const initialDelay = 600;
    let currentFrame = 0;
    let isAnimating = false;

    const animate = () => {
      if (isAnimating) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        currentFrame++;

        const revealCount = Math.min(
          Math.floor(currentFrame / framesPerChar),
          companyName.length,
        );

        // Calculate font size based on available space
        // Ensure it fits within both width and height constraints
        const fontSize = Math.min(
          Math.max(canvas.width * 0.1, 24), // Width constraint (min 24px)
          canvas.height * 0.8, // Height constraint (80% of container height)
        );

        // Calculate total text width for centering
        let totalTextWidth = 0;
        parts.forEach((part) => {
          ctx.font = `${part.weight} ${fontSize}px "Inter", sans-serif`;
          totalTextWidth += ctx.measureText(part.text).width;
        });

        let xOffset = (canvas.width - totalTextWidth) / 2;
        let charIndex = 0;

        for (const part of parts) {
          ctx.font = `${part.weight} ${fontSize}px "Inter", sans-serif`;
          for (let i = 0; i < part.text.length; i++) {
            const char = part.text[i];
            const isRevealed = charIndex < revealCount;

            if (isRevealed) {
              ctx.fillStyle = part.color;
              ctx.shadowColor =
                part.color === '#1e638d'
                  ? 'rgba(30, 99, 141, 0.3)'
                  : 'transparent';
              ctx.shadowBlur = part.color === '#1e638d' ? 8 : 0;
              ctx.fillText(char, xOffset, canvas.height / 2);
            }

            xOffset += ctx.measureText(char).width;
            charIndex++;
          }
        }

        if (revealCount >= companyName.length) {
          isAnimating = false;
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    const setupCanvas = () => {
      // Match canvas internal resolution to its display size to prevent distortion
      const parent = canvas.parentElement;
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;

      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      setTimeout(() => {
        isAnimating = true;
        currentFrame = 0;
      }, initialDelay);

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    setupCanvas();
    window.addEventListener('resize', setupCanvas);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', setupCanvas);
    };
  }, []);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: -40 }}
      transition={{ duration: 1, ease: 'easeOut' }}
      className="flex justify-center items-center mb-8 sm:mb-10 gap-1 sm:gap-2"
    >
      <motion.div
        className="flex-shrink-0"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 1,
          delay: 0.5,
          ease: 'easeOut',
        }}
      >
        <div className="relative">
          <img
            alt="SecureOneLabs"
            src={secureOneLabsLogo}
            className="w-16 h-16 sm:w-28 sm:h-28"
          />
        </div>
      </motion.div>

      {!onlyLogo &&
        <div className="relative h-16 sm:h-28 w-72 sm:w-[500px]">
          {/* Canvas text with wider letter spacing on mobile */}
          <canvas
            ref={canvasRef}
            className="absolute inset-0 z-10 w-full h-full tracking-wide sm:tracking-normal"
          />
        </div>}
    </motion.div>
  );
}
